import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { Box, Button, IconButton, InputAdornment, Radio, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { noWheelInputProps } from 'util/utils';

const renumber = (sizes) => sizes.map((size, index) => ({ ...size, sort: index }));

/** Size rows for a dish, in the shape NewSizes writes: {id, name, price, default, groups, sort}. */
const SizesEditor = ({ sizes, onChange, currencySymbol }) => {
  const update = (index, patch) => onChange(renumber(sizes.map((size, i) => (i === index ? { ...size, ...patch } : size))));

  // At most one size is the default; choosing it again clears it.
  const setDefault = (index) =>
    onChange(renumber(sizes.map((size, i) => ({ ...size, default: i === index ? !size.default : false }))));

  const add = () => onChange(renumber([...sizes, { id: uuid(), name: '', default: false, groups: [], price: '' }]));
  const remove = (index) => onChange(renumber(sizes.filter((size, i) => i !== index)));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Sizes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sizes.length === 0 ? 'Add sizes when this dish comes in more than one portion.' : 'Pick the size that is selected by default.'}
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={add}>
          Add size
        </Button>
      </Box>
      {sizes.map((size, index) => (
        <Box key={size.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Tooltip title="Selected by default">
            <Radio
              checked={!!size.default}
              onClick={() => setDefault(index)}
              inputProps={{ 'aria-label': `Make ${size.name || 'this size'} the default` }}
              sx={{ mt: 0.5 }}
            />
          </Tooltip>
          <TextField
            label="Size name"
            size="small"
            value={size.name || ''}
            onChange={(event) => update(index, { name: event.target.value })}
            error={!size.name}
            sx={{ flex: 2 }}
          />
          <TextField
            label="Price"
            size="small"
            type="number"
            value={size.price ?? ''}
            onChange={(event) => update(index, { price: event.target.value })}
            error={size.price === '' || size.price === undefined || Number(size.price) < 0}
            InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
            inputProps={{ ...noWheelInputProps, min: 0 }}
            sx={{ flex: 1 }}
          />
          <IconButton aria-label={`Remove size ${size.name}`} onClick={() => remove(index)} sx={{ mt: 0.25 }}>
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

SizesEditor.propTypes = {
  sizes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string.isRequired,
};

export default SizesEditor;
